import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@bw/shared/hooks";
import { validateReturnUrl } from "@bw/shared/utils";

/**
 * useAuthRedirect (player) — redirect an ALREADY-authenticated visitor away from
 * a public auth page (/login, /register) to where they were headed.
 *
 * Mirrors the business app's useLoginRedirect MECHANISM (returnUrl resolution +
 * open-redirect safety via validateReturnUrl) but with NO claim gate: the player
 * app has no role requirement, so any authenticated user is a valid player and
 * resolves straight to the returnUrl (or "/"). It never bounces to a
 * role-mismatch route.
 *
 * Session, sign-in, and sign-out are owned entirely by @bw/shared + the BFF;
 * this hook only decides *where* an authed user lands. Zero auth logic here.
 *
 * @param defaultReturnUrl where to send the user when no ?returnUrl is present
 *   (defaults to "/"). The value is itself validated, so a caller cannot use it
 *   as an open-redirect bypass.
 */
export function useAuthRedirect(defaultReturnUrl = "/"): void {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const hasRedirected = useRef(false);

  // Capture the requested returnUrl from the FIRST render so a later
  // re-render (e.g. the navigate itself changing the URL) can't lose it.
  const requestedReturnUrl = useRef<string | null>(null);
  if (requestedReturnUrl.current === null) {
    requestedReturnUrl.current = searchParams.get("returnUrl");
  }

  useEffect(() => {
    if (loading || !user || hasRedirected.current) return;
    hasRedirected.current = true;

    // validateReturnUrl rejects open-redirect payloads (//evil.com,
    // https://evil.com, javascript:, encoded double-slash, backslash) and
    // returns "/" for anything unsafe or absent.
    const requested = requestedReturnUrl.current;
    const target =
      requested !== null
        ? validateReturnUrl(requested)
        : validateReturnUrl(defaultReturnUrl);

    navigate(target, { replace: true });
  }, [user, loading, navigate, defaultReturnUrl]);
}
